'use client';

import { c as _c } from "react/compiler-runtime";
import type * as React from 'react';
import { cva } from 'class-variance-authority';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Select as SelectPrimitive } from 'radix-ui';
import { cn } from './lib/cn';
const selectTriggerVariants = cva(`flex h-12 px-3 transition-all w-full text-[14px] text-role-on-background rounded-2xl bg-role-background
  border border-role-outline-subtle
  not-placeholder-shown:border-role-info-outline
  hover:border-role-outline-subtle hover:bg-[#f5f5f5]
  focus-visible:bg-role-background focus-visible:border-role-outline-subtle focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-role-info-outline
  data-[state=open]:ring-2 data-[state=open]:ring-offset-2 data-[state=open]:ring-role-info-outline
  disabled:cursor-not-allowed disabled:border-role-disabled disabled:bg-role-disabled disabled:text-role-on-disabled
  shadow-none outline-hidden focus-visible:shadow-none
  box-border
  items-center justify-between
  [&>span]:truncate
  data-placeholder:text-role-outline-subtle
  [&[data-state=open]>svg]:rotate-180`, {
  variants: {
    error: {
      true: 'border-role-error-outline not-placeholder-shown:border-role-error-outline hover:border-role-error-outline focus-visible:border-role-error-outline focus-visible:ring-role-error-outline data-[state=open]:ring-role-error-outline',
      false: ''
    }
  },
  defaultVariants: {}
});
const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;
interface SelectTriggerProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  error?: boolean;
}
const SelectTrigger = t0 => {
  const $ = _c(15);
  let children;
  let className;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      error: t1,
      children,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = ref;
    $[5] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    ref = $[4];
    t1 = $[5];
  }
  const error = t1 === undefined ? false : t1;
  let t2;
  if ($[6] !== className || $[7] !== error) {
    t2 = cn(selectTriggerVariants({
      error,
      className
    }));
    $[6] = className;
    $[7] = error;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SelectPrimitive.Icon asChild={true}><ChevronDown className="h-4 w-4 min-w-4" /></SelectPrimitive.Icon>;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== children || $[11] !== props || $[12] !== ref || $[13] !== t2) {
    t4 = <SelectPrimitive.Trigger className={t2} ref={ref} {...props}>{children}{t3}</SelectPrimitive.Trigger>;
    $[10] = children;
    $[11] = props;
    $[12] = ref;
    $[13] = t2;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
const SelectScrollUpButton = t0 => {
  const $ = _c(11);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex cursor-default items-center justify-center py-1", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronUp className="h-4 w-4" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== ref || $[9] !== t1) {
    t3 = <SelectPrimitive.ScrollUpButton className={t1} ref={ref} {...props}>{t2}</SelectPrimitive.ScrollUpButton>;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
const SelectScrollDownButton = t0 => {
  const $ = _c(11);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex cursor-default items-center justify-center py-1", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronDown className="h-4 w-4" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== ref || $[9] !== t1) {
    t3 = <SelectPrimitive.ScrollDownButton className={t1} ref={ref} {...props}>{t2}</SelectPrimitive.ScrollDownButton>;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
const SelectContent = t0 => {
  const $ = _c(22);
  let children;
  let className;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      position: t1,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = ref;
    $[5] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    ref = $[4];
    t1 = $[5];
  }
  const position = t1 === undefined ? "popper" : t1;
  const t2 = position === "popper" && "data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1";
  let t3;
  if ($[6] !== className || $[7] !== t2) {
    t3 = cn("data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-56 min-w-32 max-w-[95vw] overflow-hidden rounded-2xl border bg-white text-black shadow-md data-[state=closed]:animate-out data-[state=open]:animate-in", t2, className);
    $[6] = className;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SelectScrollUpButton />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = position === "popper" && "h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)";
  let t6;
  if ($[10] !== t5) {
    t6 = cn("p-1", t5);
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== children || $[13] !== t6) {
    t7 = <SelectPrimitive.Viewport className={t6}>{children}</SelectPrimitive.Viewport>;
    $[12] = children;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <SelectScrollDownButton />;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== position || $[17] !== props || $[18] !== ref || $[19] !== t3 || $[20] !== t7) {
    t9 = <SelectPrimitive.Portal><SelectPrimitive.Content className={t3} position={position} ref={ref} {...props}>{t4}{t7}{t8}</SelectPrimitive.Content></SelectPrimitive.Portal>;
    $[16] = position;
    $[17] = props;
    $[18] = ref;
    $[19] = t3;
    $[20] = t7;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
};
SelectContent.displayName = SelectPrimitive.Content.displayName;
const SelectLabel = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("py-1.5 pr-2 pl-2 font-semibold text-sm", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <SelectPrimitive.Label className={t1} ref={ref} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
SelectLabel.displayName = SelectPrimitive.Label.displayName;
const SelectItem = t0 => {
  const $ = _c(15);
  let children;
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = ref;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    ref = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("relative flex w-full cursor-pointer select-none items-center rounded-2xl py-1.5 pr-2 pl-8 text-base outline-hidden hover:bg-gray-medium focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center"><SelectPrimitive.ItemIndicator><Check className="h-4 w-4" /></SelectPrimitive.ItemIndicator></span>;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children) {
    t3 = <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>;
    $[8] = children;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== props || $[11] !== ref || $[12] !== t1 || $[13] !== t3) {
    t4 = <SelectPrimitive.Item className={t1} ref={ref} {...props}>{t2}{t3}</SelectPrimitive.Item>;
    $[10] = props;
    $[11] = ref;
    $[12] = t1;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
SelectItem.displayName = SelectPrimitive.Item.displayName;
const SelectSeparator = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("-mx-1 my-1 h-px bg-muted", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <SelectPrimitive.Separator className={t1} ref={ref} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;
export { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue };